import { PrivilegeKey } from "~/types";

// Checks whether a known privilege key is present in a list of privileges returned by the API, returning a plain boolean
export const hasPrivilege = (
  privileges: Array<{ key: string } | null | undefined> | null | undefined,
  key: PrivilegeKey
): boolean => Boolean(privileges?.some(p => p?.key === key));

// Privilege keys follow a `domain:verb` convention (e.g. "client:create"), with some legacy
// keys further namespaced as `domain.subdomain:verb` (e.g. "client.record:delete"). Grouping
// on the leading segment keeps those legacy sub-domains under their parent domain's heading.
// Falls back to "other" for a blank/malformed key so the group never renders an empty heading.
export const getPrivilegeDomain = (key: string): string =>
  key.split(":")[0].split(".")[0] || "other";

// Domains whose display label can't be derived by simply capitalising each word, either
// because they're abbreviations (e.g. "ACC", not "Acc") or branded names with their own
// internal casing (e.g. "HealthOne", not "Healthone").
const DOMAIN_LABEL_OVERRIDES: Record<string, string> = {
  acc: "ACC",
  primhd: "PRIMHD",
  healthone: "HealthOne",
};

export const formatPrivilegeDomainLabel = (domain: string): string =>
  domain
    .split("-")
    .map(
      word =>
        DOMAIN_LABEL_OVERRIDES[word.toLowerCase()] ?? word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(" ");
